<template>
  <sp-container
    ref="container"
    class="sp-slot-cards"
    :class="classModifiers"
    :rowgap="rowGap"
    :columngap="columnGap"
    flex
    direction-sm="row"
  >
    <EntryCard
      v-for="(entry, index) in visibleEntries"
      :key="index"
      :is-small-view="isSmall"
      :columngap="columnGap"
      :entry="entry"
      :slot-variant="slotVariant"
      :more-button-variant="moreButtonVariant"
      :card-elevation="cardElevation"
      @show-more-slots="showMoreSlots"
    />

    <sp-container v-if="showInlineFullSchedule" flex class="sp-slot-col --more-entries">
      <sp-icon v-if="!isSmall" name="event-color" size="large"></sp-icon>
      <sp-button variant="appearance-link" @click="showMoreSlots">Full Schedule</sp-button>
    </sp-container>

    <sp-container v-if="showBottomFullSchedule" flex>
      <sp-container class="sp-slot-cards__full-schedule" flex :style="visibleEntriesWidth">
        <sp-button class="sp-slot-cards__full-schedule-button" color="primary" variant="text" @click="showMoreSlots">
          <sp-icon slot="prepend" name="event-filled" size="small" />
          Full Schedule
        </sp-button>
      </sp-container>
    </sp-container>
  </sp-container>
</template>

<script setup>
import { computed, ref } from "vue";
import { useContainerBreakpoints } from "../../composables/container-breakpoints";
import { toCssSpacingProperty, toJSON } from "../../utils/props";
import EntryCard from "./components/EntryCard.ce.vue";

const emit = defineEmits(["show-more-slots"]);

const props = defineProps({
  /**
   * The entries to display.
   *
   * @type {Array|String} If a string is provided, it will be parsed as JSON.
   * @default []
   */
  entries: {
    type: [Array, String],
    default: () => [],
  },
  /**
   * The maximum number of entries to display.
   * If the number of entries exceeds this value, a "Show More" button will be displayed.
   *
   * @type {Number}
   * @default 3
   */
  maxEntries: {
    type: Number,
    default: 3,
  },
  slotVariant: {
    type: String,
    default: "outlined",
  },
  moreButtonVariant: {
    type: String,
    default: "outlined",
  },
  cardElevation: {
    type: Number,
    default: undefined,
    validator: (value) => [1, 2, 3].includes(value),
  },
  fullSchedulePlacement: {
    type: String,
    default: "inline",
    validator: (value) => ["inline", "bottom"].includes(value),
  },
});

const container = ref(null);
const { isSmall } = useContainerBreakpoints(container, { small: { max: 490 } });

const rowGap = toCssSpacingProperty(12);
const columnGap = computed(() => toCssSpacingProperty(isSmall.value ? 4 : 8));

/**
 * The prop `entries` as an array of objects.
 *
 * @type {import('vue').ComputedRef<Array<Object>>}
 */
const entries = computed(() => toJSON(props.entries, true));
const visibleEntries = computed(() => entries.value.slice(0, props.maxEntries));
const hasMoreEntries = computed(() => entries.value.length > visibleEntries.value.length);
const isBottomFullSchedule = computed(() => props.fullSchedulePlacement === "bottom");
const showInlineFullSchedule = computed(() => hasMoreEntries.value && !isBottomFullSchedule.value);
const showBottomFullSchedule = computed(() => hasMoreEntries.value && isBottomFullSchedule.value);

const classModifiers = computed(() => ({
  "--is-small-view": isSmall.value,
  "--full-schedule-bottom": isBottomFullSchedule.value && !isSmall.value,
}));

const visibleEntriesWidth = computed(() => {
  const n = visibleEntries.value.length;
  return {
    maxWidth: `calc(var(--sp-comp-slot-cards-card-width, 12rem) * ${n} + (${n - 1}) * ${columnGap.value})`,
  };
});

function showMoreSlots({ date }) {
  emit("show-more-slots", { date });
}
</script>

<style lang="scss">
:host {
  display: block;
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-slot-cards {
  --row-column-gap: v-bind(columnGap);

  &.--is-small-view {
    --sp-comp-container-justify-content: space-around;
  }

  &.--full-schedule-bottom {
    --sp-comp-container-justify-content: flex-end;
  }

  &__full-schedule {
    --sp-comp-container-justify-content: center;
  }

  &__full-schedule-button {
    width: auto;
    --sp-comp-button-font-weight: var(
      --sp-comp-slot-cards-full-schedule-button-font-weight,
      var(--sp-comp-button-font-weight)
    );
  }
}

.sp-slot-col.--more-entries {
  --sp-comp-container-align-items: center;
  max-width: var(--sp-comp-slot-cards-card-width, 193px);
  width: 100%;
  align-self: center;
}
</style>
